import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, differenceInDays, addDays, parseISO } from 'date-fns';
import { 
  CalendarIcon, 
  Users, 
  Check, 
  Tag, 
  CheckCircle2
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { formatDateLong } from '../lib/dates';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import InfoTip from '../components/InfoTip';
import { money } from '../lib/money';
import { isTooLateForCheckIn, minStayFor, isWithinMinStay } from '../lib/bookingRules';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Χρέωση δεύτερου οχήματος ανά διανυκτέρευση (EUR) — το πρώτο όχημα σταθμεύει δωρεάν
const VEHICLE_FEE_PER_NIGHT = 5;

// Ανακοίνωση κλεισίματος αεροδρομίου: εμφανίζεται για άφιξη 15/1/2027 – 28/1/2027 (συμπεριλαμβανομένων)
const AIRPORT_NOTICE_FROM = '2027-01-15';
const AIRPORT_NOTICE_TO = '2027-01-28';

const Booking = () => {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  
  // Form state
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState('2');
  
  // Guest details
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [message, setMessage] = useState('');
  // Κατοικίδιο Ναι/Όχι: υποχρεωτική επιλογή (null = δεν επιλέχθηκε), χωρίς χρέωση
  const [hasPet, setHasPet] = useState(null);
  const [petError, setPetError] = useState(false);
  const [hasSecondVehicle, setHasSecondVehicle] = useState(false);
  // Κωδικός έκπτωσης (προαιρετικός): ελέγχεται ΜΟΝΟ από τον server μέσω calculate-price
  const [discountCode, setDiscountCode] = useState('');
  const [debouncedCode, setDebouncedCode] = useState('');
  const [codeError, setCodeError] = useState(false);
  
  // Data state
  const [blockedDates, setBlockedDates] = useState([]);
  // Ελάχιστη διαμονή ανά ημέρα άφιξης (από Smoobu/PriceLabs)
  const [minStays, setMinStays] = useState({});
  const [pricing, setPricing] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  // Initialize from URL params
  useEffect(() => {
    const checkInParam = searchParams.get('checkIn');
    const checkOutParam = searchParams.get('checkOut');
    const guestsParam = searchParams.get('guests');
    
    if (checkInParam) setCheckIn(parseISO(checkInParam));
    if (checkOutParam) setCheckOut(parseISO(checkOutParam));
    // Μόνο 1–8 άτομα· αλλιώς μένει η προεπιλογή 2
    if (/^[1-8]$/.test(guestsParam || '')) setGuests(guestsParam);
  }, [searchParams]);

  // Fetch blocked dates
  useEffect(() => {
    const fetchData = async () => {
      try {
        const blockedRes = await axios.get(`${API}/blocked-dates`);
        setBlockedDates(blockedRes.data.blocked_dates.map(d => new Date(d)));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
    axios.get(`${API}/min-stays`)
      .then((res) => setMinStays(res.data.min_stays || {}))
      .catch((error) => console.error('Error fetching min stays:', error));
  }, []);

  // Ο κωδικός στέλνεται στον server λίγο μετά το τελευταίο πλήκτρο (όχι ανά χαρακτήρα)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedCode(discountCode.trim()), 400);
    return () => clearTimeout(timer);
  }, [discountCode]);

  // Calculate price when relevant fields change
  useEffect(() => {
    const calculatePrice = async () => {
      if (checkIn && checkOut) {
        setIsLoading(true);
        const params = {
          check_in: format(checkIn, 'yyyy-MM-dd'),
          check_out: format(checkOut, 'yyyy-MM-dd'),
          guests: parseInt(guests),
        };
        try {
          const response = await axios.post(`${API}/calculate-price`, null, {
            params: debouncedCode ? { ...params, discount_code: debouncedCode } : params,
          });
          setPricing(response.data);
          setCodeError(false);
        } catch (error) {
          if (error.response?.data?.detail?.code === 'invalid_discount_code') {
            // Άκυρος κωδικός: μήνυμα + σύνοψη χωρίς έκπτωση μέχρι να διορθωθεί ή σβηστεί
            setCodeError(true);
            try {
              const response = await axios.post(`${API}/calculate-price`, null, { params });
              setPricing(response.data);
            } catch (fallbackError) {
              console.error('Error calculating price:', fallbackError);
              setPricing(null);
            }
          } else {
            console.error('Error calculating price:', error);
            setPricing(null);
          }
        }
        setIsLoading(false);
      }
    };
    calculatePrice();
  }, [checkIn, checkOut, guests, debouncedCode]);

  const isDateBlocked = (date) => {
    return blockedDates.some(
      (blocked) => format(blocked, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
    );
  };

  // Μέγιστη επιτρεπτή ημερομηνία αναχώρησης: η πρώτη κατειλημμένη ΝΥΧΤΑ μετά το check-in.
  // Η ημέρα αναχώρησης δεν είναι διανυκτέρευση (turnover day), οπότε επιτρέπεται.
  const maxCheckOut = () => {
    if (!checkIn) return null;
    let d = new Date(checkIn);
    for (let i = 0; i < 400; i++) {
      if (isDateBlocked(d)) return d;
      d = addDays(d, 1);
    }
    return null;
  };

  const nights = checkIn && checkOut ? differenceInDays(checkOut, checkIn) : 0;
  const checkInStr = checkIn ? format(checkIn, 'yyyy-MM-dd') : null;
  const showAirportNotice = !!checkInStr && checkInStr >= AIRPORT_NOTICE_FROM && checkInStr <= AIRPORT_NOTICE_TO;
  const vehicleFee = hasSecondVehicle ? VEHICLE_FEE_PER_NIGHT * nights : 0;
  const grandTotal = pricing ? pricing.total + vehicleFee : 0;
  // Η σύνοψη δεν αντιστοιχεί ακόμα στον κωδικό του πεδίου (αναμονή απάντησης server)
  const codeNorm = discountCode.replace(/\s+/g, '').toUpperCase();
  const codePending = !codeError && (pricing?.discount_code || '') !== codeNorm;
  const discountAmount = pricing?.discount_amount || 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!checkIn || !checkOut || !guestName || !guestEmail || !guestPhone) {
      toast.error(t('toasts.fillRequired'));
      return;
    }
    if (hasPet === null) {
      setPetError(true);
      toast.error(t('booking.petRequired'));
      return;
    }
    // Προθεσμία 6 ωρών: ίδια μέρα μόνο έως 10:00 ώρα Ελλάδας
    if (isTooLateForCheckIn(checkIn)) {
      toast.error(t('booking.sameDayError'));
      return;
    }
    // Ελάχιστη διαμονή για την ημέρα άφιξης
    const minStay = minStayFor(minStays, checkIn);
    if (nights < minStay) {
      toast.error(t('booking.minStayError').replace('{n}', minStay));
      return;
    }
    // Άκυρος κωδικός έκπτωσης: η κράτηση μπλοκάρεται μέχρι να διορθωθεί ή να σβηστεί
    if (codeError || codePending) {
      toast.error(t('booking.discountInvalid'));
      return;
    }
    // Έλεγχος διαθεσιμότητας: καμία νύχτα του εύρους να μην είναι κατειλημμένη
    let checkDate = new Date(checkIn);
    while (checkDate < checkOut) {
      if (isDateBlocked(checkDate)) {
        toast.error(t('toasts.datesUnavailable'));
        return;
      }
      checkDate = addDays(checkDate, 1);
    }
    
    setIsSubmitting(true);
    
    try {
      // Δεύτερο όχημα: σημείωση στο message (το κατοικίδιο πάει στο πεδίο has_pet)
      const vehicleNote = hasSecondVehicle
        ? (language === 'gr'
            ? `[Δεύτερο όχημα — ${VEHICLE_FEE_PER_NIGHT}€/διανυκτέρευση × ${nights} = ${vehicleFee}€]`
            : `[Second vehicle — €${VEHICLE_FEE_PER_NIGHT}/night × ${nights} = €${vehicleFee}]`)
        : '';
      const submittedMessage = [message, vehicleNote].filter(Boolean).join('\n\n');

      await axios.post(`${API}/bookings`, {
        check_in: format(checkIn, 'yyyy-MM-dd'),
        check_out: format(checkOut, 'yyyy-MM-dd'),
        guests: parseInt(guests),
        guest_name: guestName,
        guest_email: guestEmail,
        guest_phone: guestPhone,
        message: submittedMessage,
        discount_code: discountCode.trim() || null,
        estimated_price: grandTotal, // μόνο για έλεγχο· ο server ξαναϋπολογίζει την τιμή
        second_vehicle: hasSecondVehicle,
        language: language,
        has_pet: hasPet,
      });
      
      setBookingSuccess(true);
      toast.success(t('booking.success'));
    } catch (error) {
      console.error('Error submitting booking:', error);
      if (error.response?.data?.detail?.code === 'invalid_discount_code') {
        setCodeError(true);
        toast.error(t('booking.discountInvalid'));
      } else {
        toast.error(t('toasts.bookingError'));
      }
    }
    
    setIsSubmitting(false);
  };

  if (bookingSuccess) {
    return (
      <div data-testid="booking-success" className="min-h-screen pt-32 pb-20 bg-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-[#1E3A5F]" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl mb-4 text-[#1E3A5F]">
            {t('booking.success')}
          </h1>
          <p className="text-muted-foreground text-lg mb-8">
            {t('booking.successMessage')}
          </p>
          <div className="bg-[#f8fafc] rounded-sm p-6 text-left mb-8 border border-[#1E3A5F]/10">
            <h3 className="font-semibold mb-4 text-[#1E3A5F]">{t('booking.yourBookingEnquiry')}</h3>
            <div className="space-y-2 text-sm">
              <p><span className="text-muted-foreground">{t('booking.dates')}:</span> {checkIn && formatDateLong(checkIn, language)} - {checkOut && formatDateLong(checkOut, language)}</p>
              <p><span className="text-muted-foreground">{t('booking.guests')}:</span> {guests}</p>
              <p><span className="text-muted-foreground">{t('booking.petFee')}:</span> {hasPet ? t('booking.yes') : t('booking.no')}</p>
              {hasSecondVehicle && (
                <p><span className="text-muted-foreground">{t('booking.vehicleFee')}:</span> {money(VEHICLE_FEE_PER_NIGHT, language)} × {nights}{' '}{nights === 1 ? t('booking.night') : t('booking.nights')} = {money(vehicleFee, language)}</p>
              )}
              {discountAmount > 0 && (
                <p><span className="text-muted-foreground">{t('booking.discountCodeLine').replace('{code}', pricing.discount_code)}:</span> −{money(discountAmount, language)}</p>
              )}
              <p><span className="text-muted-foreground">{t('booking.estimatedTotal')}:</span> {money(grandTotal, language)} <span className="text-xs text-muted-foreground">({t('booking.subjectToConfirmation')})</span></p>
            </div>
          </div>
          <Button
            data-testid="back-to-home-btn"
            onClick={() => window.location.href = '/'}
            className="bg-[#1E3A5F] hover:bg-[#1E3A5F]/90 text-white"
          >
            {t('booking.backToHome')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="min-h-screen pt-24 pb-32 bg-white">
      <Toaster position="top-center" richColors />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="uppercase tracking-widest text-xs text-[#D4A72C] mb-4">
            {t('booking.bookYourStay')}
          </p>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#1E3A5F]">
            {t('booking.requestBooking')}
          </h1>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
            {t('booking.bookingSubtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Date & Guest Selection */}
            <div className="bg-white border border-border rounded-sm p-6 md:p-8">
              <h2 className="font-serif text-xl mb-6 text-[#1E3A5F]">{t('booking.selectYourDates')}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Range Picker — Check-in + Check-out σε ένα */}
                <div className="md:col-span-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      data-testid="booking-dates-trigger"
                      className="flex items-center gap-3 p-4 border border-border rounded-sm hover:border-[#D4A72C]/50 transition-colors text-left w-full"
                    >
                      <CalendarIcon className="w-5 h-5 text-[#D4A72C]" />
                      <div className="flex gap-6">
                        <div>
                          <p className="text-xs text-[#1E3A5F]/60 uppercase tracking-wider">{t('booking.checkIn')}</p>
                          <p className="text-sm font-medium text-[#1E3A5F]">
                            {checkIn ? formatDateLong(checkIn, language) : t('booking.selectDate')}
                          </p>
                        </div>
                        <div className="border-l border-[#1E3A5F]/10 pl-6">
                          <p className="text-xs text-[#1E3A5F]/60 uppercase tracking-wider">{t('booking.checkOut')}</p>
                          <p className="text-sm font-medium text-[#1E3A5F]">
                            {checkOut ? formatDateLong(checkOut, language) : t('booking.selectDate')}
                          </p>
                        </div>
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      numberOfMonths={2}
                      selected={{ from: checkIn, to: checkOut }}
                      onSelect={(range) => {
                        setCheckIn(range?.from || null);
                        // Αποδεκτό check-out μόνο αν καλύπτει την ελάχιστη διαμονή
                        if (range?.from && range?.to && range.to > range.from &&
                            differenceInDays(range.to, range.from) >= minStayFor(minStays, range.from)) {
                          setCheckOut(range.to);
                        } else {
                          setCheckOut(null);
                        }
                      }}
                      disabled={(date) => {
                        if (checkIn && !checkOut && date > checkIn) {
                          // Επιλογή check-out: όχι νωρίτερα από check-in + ελάχιστη διαμονή
                          if (isWithinMinStay(minStays, checkIn, date)) return true;
                          const mc = maxCheckOut();
                          if (mc && format(date, 'yyyy-MM-dd') === format(mc, 'yyyy-MM-dd')) return false;
                          return isDateBlocked(date);
                        }
                        // Check-in: παρελθόν ή σήμερα μετά τις 10:00 Ελλάδας κλειδωμένα
                        if (isTooLateForCheckIn(date)) return true;
                        return isDateBlocked(date);
                      }}
                      defaultMonth={checkIn || new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                </div>

                {/* Guests - Max 8 */}
                <div className="flex items-center gap-3 p-4 border border-border rounded-sm">
                  <Users className="w-5 h-5 text-[#D4A72C]" />
                  <div className="flex-1">
                    <p className="text-xs text-[#1E3A5F]/60 uppercase tracking-wider">{t('booking.guests')}</p>
                    <Select value={guests} onValueChange={setGuests}>
                      <SelectTrigger data-testid="booking-form-guests" className="border-0 p-0 h-auto shadow-none text-[#1E3A5F]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                          <SelectItem key={num} value={num.toString()}>
                            {num} {num === 1 ? t('booking.guest') : t('booking.guests')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Ανακοίνωση αεροδρομίου — μόνο για άφιξη 15–28/1/2027 */}
              {showAirportNotice && (
                <p data-testid="booking-airport-notice" className="mt-4 text-xs text-[#1E3A5F]/70 italic border-l-2 border-[#D4A72C] pl-3">
                  {t('booking.airportNotice')}
                </p>
              )}

              {/* Pet option: Ναι/Όχι υποχρεωτικό, χωρίς χρέωση */}
              <fieldset
                data-testid="booking-pet-field"
                className={`mt-4 p-4 border rounded-sm ${petError ? 'border-red-500' : 'border-border'}`}
              >
                <legend className="text-sm font-medium text-[#1E3A5F] px-1">
                  {t('booking.petQuestion')} *
                </legend>
                <div className="flex gap-6 mt-1">
                  {[{ value: true, label: t('booking.yes'), id: 'yes' }, { value: false, label: t('booking.no'), id: 'no' }].map((opt) => (
                    <label key={opt.id} className="flex items-center gap-2 text-sm text-[#1E3A5F] cursor-pointer">
                      <input
                        type="radio"
                        name="booking-pet"
                        data-testid={`booking-pet-${opt.id}`}
                        checked={hasPet === opt.value}
                        onChange={() => { setHasPet(opt.value); setPetError(false); }}
                        className="accent-[#1E3A5F]"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-[#1E3A5F]/60 mt-2">{t('booking.petNote')}</p>
                {petError && (
                  <p data-testid="booking-pet-error" className="text-xs text-red-600 mt-2">{t('booking.petRequired')}</p>
                )}
              </fieldset>

              {/* Second vehicle option */}
              <div className="mt-4 flex items-start gap-3 p-4 border border-border rounded-sm">
                <Checkbox
                  id="booking-vehicle"
                  data-testid="booking-vehicle-checkbox"
                  checked={hasSecondVehicle}
                  onCheckedChange={(checked) => setHasSecondVehicle(checked === true)}
                  className="mt-1"
                />
                <div>
                  <label
                    htmlFor="booking-vehicle"
                    className="text-sm font-medium text-[#1E3A5F] cursor-pointer"
                  >
                    {t('booking.vehicleQuestion')}
                  </label>
                  <InfoTip text={t('booking.vehicleInfo')} testId="booking-vehicle-info" />
                </div>
              </div>
            </div>
            {/* Guest Details Form */}
            <div className="bg-white border border-border rounded-sm p-6 md:p-8">
              <h2 className="font-serif text-xl mb-6 text-[#1E3A5F]">{t('booking.yourDetails')}</h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-[#1E3A5F]/70 mb-1">{t('booking.fullName')} *</label>
                  <Input
                    data-testid="guest-name-input"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#1E3A5F]/70 mb-1">{t('booking.email')} *</label>
                    <Input
                      data-testid="guest-email-input"
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#1E3A5F]/70 mb-1">{t('booking.phone')} *</label>
                    <Input
                      data-testid="guest-phone-input"
                      type="tel"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="booking-discount-code" className="block text-sm text-[#1E3A5F]/70 mb-1">{t('booking.discountCodeLabel')}</label>
                  <Input
                    id="booking-discount-code"
                    data-testid="discount-code-input"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value)}
                    placeholder={t('booking.enterCode')}
                    autoComplete="off"
                    aria-invalid={codeError}
                    className={codeError ? 'border-red-500' : ''}
                  />
                  {codeError && (
                    <p data-testid="discount-code-error" className="text-xs text-red-600 mt-1">{t('booking.discountInvalid')}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-[#1E3A5F]/70 mb-1">{t('booking.message')}</label>
                  <Textarea
                    data-testid="guest-message-input"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    placeholder={t('booking.specialRequestsPlaceholder')}
                  />
                </div>
                
                <Button
                  data-testid="submit-booking-btn"
                  type="submit"
                  disabled={isSubmitting || !checkIn || !checkOut || codeError || codePending}
                  className="w-full bg-[#1E3A5F] hover:bg-[#1E3A5F]/90 text-white py-6 btn-lift"
                >
                  {isSubmitting ? t('booking.submitting') : t('booking.submit')}
                </Button>
                
                <p className="text-xs text-center text-[#1E3A5F]/60">
                  {t('booking.noChargeYet')}
                </p>
              </form>
            </div>
          </div>

          {/* Price Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-border rounded-sm p-6 sticky top-28">
              <h3 className="font-serif text-xl mb-6 text-[#1E3A5F]">
                {t('booking.priceSummary')}
                <InfoTip text={t('booking.priceInfo')} testId="booking-price-info" />
              </h3>
              
              {pricing && nights > 0 ? (
                <div className="space-y-4">
                  {pricing.nightly_rates && pricing.nightly_rates.map((rate, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-[#1E3A5F]/60">{rate.date}</span>
                      <span className="text-[#1E3A5F]">{money(rate.price, language)}</span>
                    </div>
                  ))}
                  {pricing.length_discount_percent > 0 && (
                    <div className="flex justify-between text-sm pt-1 border-t border-border/50">
                      <span className="text-green-600">{t('booking.lengthDiscount')} (-{pricing.length_discount_percent}%)</span>
                      <span className="text-green-600">✓</span>
                    </div>
                  )}
                  {/* ΤΑΚΚ με ανάλυση: €/νύχτα × νύχτες = σύνολο */}
                  <div data-testid="booking-env-fee-line" className="text-sm pt-1 border-t border-border/50">
                    <div className="flex justify-between">
                      <span className="text-[#1E3A5F]/60">
                        {t('booking.envFee')}
                        <InfoTip text={t('booking.envFeeInfo')} testId="booking-env-fee-info" />
                      </span>
                      <span className="text-[#1E3A5F]">{money(pricing.env_fee_total, language)}</span>
                    </div>
                    <p className="text-xs text-[#1E3A5F]/50 mt-0.5">
                      {t('booking.envFeeShort')}: {money(pricing.env_fee_per_night, language)} × {pricing.nights}{' '}
                      {pricing.nights === 1 ? t('booking.night') : t('booking.nights')} = {money(pricing.env_fee_total, language)}
                    </p>
                  </div>
                  
                  {hasSecondVehicle && (
                    <div data-testid="booking-vehicle-fee-line" className="flex justify-between text-sm pt-1 border-t border-border/50">
                      <span className="text-[#1E3A5F]/60">
                        {t('booking.vehicleFee')}: {money(VEHICLE_FEE_PER_NIGHT, language)} × {nights}{' '}
                        {nights === 1 ? t('booking.night') : t('booking.nights')}
                      </span>
                      <span className="text-[#1E3A5F]">{money(vehicleFee, language)}</span>
                    </div>
                  )}

                  {/* Κωδικός έκπτωσης: τιμή πριν, γραμμή κωδικού, τελική τιμή από κάτω */}
                  {discountAmount > 0 && (
                    <>
                      <div data-testid="booking-price-before-discount" className="flex justify-between text-sm pt-1 border-t border-border/50">
                        <span className="text-[#1E3A5F]/60">{t('booking.priceBeforeDiscount')}</span>
                        <span className="text-[#1E3A5F]">{money(grandTotal + discountAmount, language)}</span>
                      </div>
                      <div data-testid="booking-discount-line" className="flex justify-between text-sm">
                        <span className="text-green-600">{t('booking.discountCodeLine').replace('{code}', pricing.discount_code)}</span>
                        <span className="text-green-600">−{money(discountAmount, language)}</span>
                      </div>
                    </>
                  )}

                  <div className="pt-4 border-t border-border flex justify-between font-semibold text-lg">
                    <span className="text-[#1E3A5F]">{t('booking.total')}</span>
                    <span className="text-[#1E3A5F]">{money(grandTotal, language)}</span>
                  </div>
                  

                </div>
              ) : (
                <div className="text-center py-8">
                  <CalendarIcon className="w-12 h-12 mx-auto text-[#1E3A5F]/20 mb-4" />
                  <p className="text-[#1E3A5F]/60">
                    {t('booking.selectDatesToSeePricing')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;
